import React from 'react';
import { MagnifyingGlassIcon, XMarkIcon, TrashIcon, DocumentTextIcon, CurrencyDollarIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import LoadingTables from '../../../global/LoadingTables';
import Pager from '../../../global/Pager';
import { moneyFormat } from '../../../../utils/format';
import DetalleTakeoff from '../Modales/DetalleTakeoff';

const estadoOptions = [
  { value: '0', label: 'Revision' },
  { value: '1', label: 'Ok' },
  { value: '2', label: 'Error' },
];

const resolveValue = (obj, keys, fallback = '') => {
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (obj && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return fallback;
};

const getObservaciones = (row) => {
  if (!row) {
    return '';
  }
  const direct = resolveValue(
    row,
    ['ERROR_MSG', 'error_msg', 'OBSERVACIONES', 'observaciones', 'errors', 'errores'],
    ''
  );
  if (direct) {
    return Array.isArray(direct) ? direct.join(', ') : String(direct);
  }
  const rulesFailed = row?.ERROR_JSON?.rules_failed || row?.error_json?.rules_failed;
  if (Array.isArray(rulesFailed) && rulesFailed.length > 0) {
    return rulesFailed.join('; ');
  }
  const takeoff = row?.takeoff;
  if (takeoff && takeoff !== row) {
    const nested = resolveValue(
      takeoff,
      ['ERROR_MSG', 'error_msg', 'OBSERVACIONES', 'observaciones', 'errors', 'errores'],
      ''
    );
    if (nested) {
      return Array.isArray(nested) ? nested.join(', ') : String(nested);
    }
  }
  return '';
};

// Helpers para acceder datos anidados
const getTakeoff = (row) => row?.takeoff || row;
const getDocumento = (row) => row?.documento || {};
const getSolicitud = (row) => row?.solicitud || {};
const getVehiculos = (row) => row?.solicitud?.vehiculos || [];

// Cálculo de diferencia con colores
const calcDiff = (totalTK, totalSol) => {
  const tk = parseFloat(totalTK) || 0;
  const sol = parseFloat(totalSol) || 0;
  if (sol === 0) return { percent: 0, diff: 0, color: 'text-gray-400' };
  const diff = tk - sol;
  const percent = Math.abs((diff / sol) * 100);
  let color = 'text-green-600 bg-green-50';
  if (percent >= 5) color = 'text-red-600 bg-red-50';
  else if (percent >= 1) color = 'text-yellow-600 bg-yellow-50';
  return { percent, diff, color };
};

// Formateo de fecha corta DD/MM
const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
};

// Componente para sub-tabla de vehículos
const getVehiculoTipoId = (vehiculo) =>
  vehiculo?.SOLICITUD_VEHICULO_TIPO_ID ?? vehiculo?.solicitud_vehiculo_tipo_id ?? vehiculo?.SOLICITUD_VEHICULO_ID ?? '';

const getManifiestoEstado = (vehiculo) =>
  Number(
    vehiculo?.SOLICITUD_VEHICULO_MANIFIESTO_ESTADO ?? vehiculo?.MANIFIESTO_ESTADO ?? vehiculo?.manifiesto_estado ?? 0
  );

const getManifiestoIconColor = (estado) => {
  if (estado === 1) return 'text-yellow-500';
  if (estado === 2) return 'text-green-500';
  if (estado === 9) return 'text-gray-400';
  return 'text-red-500';
};

const VehiculosSubTable = ({ vehiculos, solicitud, reporte, onOpenManifiesto, onOpenTarifas }) => {
  if (!vehiculos || vehiculos.length === 0) return null;

  return (
    <div className="border-l-4 border-blue-400 bg-blue-50/40 ml-4 my-1">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-600 bg-blue-100/50 border-b border-blue-200">
            <th className="px-2 py-1 text-left font-semibold">Placa</th>
            <th className="px-2 py-1 text-right font-semibold">Peso</th>
            <th className="px-2 py-1 text-left font-semibold">Manifiesto</th>
            <th className="px-2 py-1 text-left font-semibold">Remesa</th>
            <th className="px-2 py-1 text-left font-semibold">RNDC</th>
            <th className="px-2 py-1 text-right font-semibold">Cant Manif</th>
            <th className="px-2 py-1 text-center font-semibold">Unid</th>
            <th className="px-2 py-1 text-center font-semibold">Acc</th>
            <th className="px-2 py-1 text-left font-semibold">Grupo Tarifa</th>
            <th className="px-2 py-1 text-left font-semibold">Tarifa Nombre</th>
            <th className="px-2 py-1 text-center font-semibold">Unidad</th>
            <th className="px-2 py-1 text-right font-semibold">Cant</th>
            <th className="px-2 py-1 text-right font-semibold">Valor</th>
            <th className="px-2 py-1 text-right font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {vehiculos.map((vehiculo, vIdx) => {
            const detalles = vehiculo?.detalles || [];
            const rowCount = Math.max(detalles.length, 1);
            const manifiestoEstado = getManifiestoEstado(vehiculo);
            const iconColor = getManifiestoIconColor(manifiestoEstado);
            const isManifiestoDisabled = !onOpenManifiesto;
            const isTarifaDisabled = !onOpenTarifas;
            const manifiestoTitle = 'Confirmar manifiesto';
            const tarifaTitle = 'Gestionar tarifas';

            if (detalles.length === 0) {
              return (
                <tr key={vIdx} className="border-b border-blue-100 bg-white/50">
                  <td className="px-2 py-1 font-medium">{vehiculo?.VEHICULO_PLACA || '-'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{vehiculo?.SOLICITUD_PESO?.toLocaleString() || '-'}</td>
                  <td className="px-2 py-1 tabular-nums">{vehiculo?.MANIFIESTO_CONSECUTIVO || '-'}</td>
                  <td className="px-2 py-1 tabular-nums">{vehiculo?.MANIFIESTO_REMESA || '-'}</td>
                  <td className="px-2 py-1">{vehiculo?.MANIFIESTO_RNDC || '-'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{vehiculo?.MANIFIESTO_CANTIDAD || '-'}</td>
                  <td className="px-2 py-1 text-center">{vehiculo?.MANIFIESTO_UNIDAD || '-'}</td>
                  <td className="px-2 py-1 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => onOpenManifiesto?.(vehiculo, solicitud)}
                        disabled={isManifiestoDisabled}
                        title={manifiestoTitle}
                        className={`p-0.5 ${isManifiestoDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:text-blue-700'} ${iconColor}`}>
                        <DocumentTextIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onOpenTarifas?.(vehiculo, solicitud, reporte)}
                        disabled={isTarifaDisabled}
                        title={tarifaTitle}
                        className={`p-0.5 text-indigo-600 ${isTarifaDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:text-indigo-800'}`}>
                        <CurrencyDollarIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-1 text-gray-400" colSpan={6}>Sin detalles de tarifa</td>
                </tr>
              );
            }

            return detalles.map((detalle, dIdx) => (
              <tr key={`${vIdx}-${dIdx}`} className="border-b border-blue-100 last:border-b-0 bg-white/50 hover:bg-white">
                {dIdx === 0 && (
                  <>
                    <td className="px-2 py-1 font-medium" rowSpan={rowCount}>{vehiculo?.VEHICULO_PLACA || '-'}</td>
                    <td className="px-2 py-1 text-right tabular-nums" rowSpan={rowCount}>
                      {vehiculo?.SOLICITUD_PESO?.toLocaleString() || '-'}
                    </td>
                    <td className="px-2 py-1 tabular-nums" rowSpan={rowCount}>{vehiculo?.MANIFIESTO_CONSECUTIVO || '-'}</td>
                    <td className="px-2 py-1 tabular-nums" rowSpan={rowCount}>{vehiculo?.MANIFIESTO_REMESA || '-'}</td>
                    <td className="px-2 py-1" rowSpan={rowCount}>{vehiculo?.MANIFIESTO_RNDC || '-'}</td>
                    <td className="px-2 py-1 text-right tabular-nums" rowSpan={rowCount}>{vehiculo?.MANIFIESTO_CANTIDAD || '-'}</td>
                    <td className="px-2 py-1 text-center" rowSpan={rowCount}>{vehiculo?.MANIFIESTO_UNIDAD || '-'}</td>
                    <td className="px-2 py-1 text-center" rowSpan={rowCount}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => onOpenManifiesto?.(vehiculo, solicitud)}
                          disabled={isManifiestoDisabled}
                          title={manifiestoTitle}
                          className={`p-0.5 ${isManifiestoDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:text-blue-700'} ${iconColor}`}>
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                        onClick={() => onOpenTarifas?.(vehiculo, solicitud, reporte)}
                          disabled={isTarifaDisabled}
                          title={tarifaTitle}
                          className={`p-0.5 text-indigo-600 ${isTarifaDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:text-indigo-800'}`}>
                          <CurrencyDollarIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
                <td className="px-2 py-1">{detalle?.TARIFA_GRUPO || '-'}</td>
                <td className="px-2 py-1 text-gray-600">{detalle?.TARIFA_NOMBRE || '-'}</td>
                <td className="px-2 py-1 text-center">{detalle?.TARIFA_UNIDAD || '-'}</td>
                <td className="px-2 py-1 text-right tabular-nums">{detalle?.SOLICITUD_DETALLE_CANTIDAD}</td>
                <td className="px-2 py-1 text-right tabular-nums">{moneyFormat(detalle?.SOLICITUD_DETALLE_VALOR)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{moneyFormat(detalle?.SOLICITUD_DETALLE_TOTAL)}</td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

const TakeoffTab = (props) => {
  const { data, actions, pager } = props;
  const {
    lotes = [],
    lotesLoading = false,
    selectedLoteId = '',
    loteFilters = {},
    takeoffFilters = {},
    takeoffRows = [],
    takeoffLoading = false,
    takeoffPagination = null,
    takeoffDetail = null,
    takeoffDetailOpen = false,
    takeoffDetailLoading = false,
    takeoffUpdating = {},
    takeoffFinalizing = {},
  } = data;

  const {
    handleLoteFilterChange = () => {},
    handleLoadLotes = () => {},
    handleSelectLote = () => {},
    handleTakeoffFilterChange = () => {},
    handleTakeoffSearch = () => {},
    handleTakeoffClearFilters = () => {},
    handleCloseTakeoffDetail = () => {},
    handleUpdateTakeoffEstado = () => {},
    handleDeleteTakeoffReporte = () => {},
    handleDeleteTakeoffLote = () => {},
    handleOpenManifiesto = () => {},
    handleOpenTarifas = () => {},
    handleFinalizarTakeoff = () => {},
  } = actions;

  const { handleNextPage = () => {}, handlePreviusPage = () => {} } = pager;

  const selectedLote = lotes.find(
    (item) => String(item?.LOTE_ID ?? item?.lote_id ?? '') === String(selectedLoteId)
  );

  const handleManifiestoClick = (vehiculo, solicitud) => {
    const vehiculoTipoId = getVehiculoTipoId(vehiculo);
    const vehiculoPayload =
      vehiculo?.SOLICITUD_VEHICULO_TIPO_ID ? vehiculo : { ...vehiculo, SOLICITUD_VEHICULO_TIPO_ID: vehiculoTipoId };
    handleOpenManifiesto?.(vehiculoPayload, solicitud);
  };

  const handleTarifasClick = (vehiculo, solicitud, reporte) => {
    const vehiculoTipoId = getVehiculoTipoId(vehiculo);
    const vehiculoPayload =
      vehiculo?.SOLICITUD_VEHICULO_TIPO_ID ? vehiculo : { ...vehiculo, SOLICITUD_VEHICULO_TIPO_ID: vehiculoTipoId };
    handleOpenTarifas?.(vehiculoPayload, solicitud, reporte);
  };

  return (
    <div className="flex flex-grow flex-col overflow-hidden">
      <div className="shrink-0 flex items-center justify-end gap-3 py-2">
        <select
          value={selectedLoteId}
          onChange={(event) => handleSelectLote(event.target.value)}
          className="min-w-[180px] rounded border px-2 py-1 text-sm">
          <option value="">Seleccione lote</option>
          {lotes.map((lote, index) => {
            const loteId = lote?.LOTE_ID ?? lote?.lote_id ?? index + 1;
            const total = lote?.total ?? lote?.TOTAL ?? 0;
            return (
              <option key={loteId} value={loteId}>
                Lote {loteId} - {total}
              </option>
            );
          })}
        </select>

        <input
          type="text"
          value={takeoffFilters.reporte || ''}
          onChange={(event) => handleTakeoffFilterChange('reporte', event.target.value)}
          placeholder="Reporte"
          className="w-32 rounded border px-2 py-1 text-sm"
        />

        <select
          value={takeoffFilters.estado}
          onChange={(event) => handleTakeoffFilterChange('estado', event.target.value)}
          className="rounded border px-2 py-1 text-sm">
          <option value="">Estado</option>
          {estadoOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleTakeoffSearch}
          className="p-1 text-blue-600 hover:text-blue-800"
          title="Buscar">
          <MagnifyingGlassIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleTakeoffClearFilters}
          className="p-1 text-gray-600 hover:text-gray-800"
          title="Limpiar">
          <XMarkIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleDeleteTakeoffLote}
          disabled={!selectedLoteId}
          className={`p-1 ${selectedLoteId ? 'text-red-600 hover:text-red-800' : 'text-gray-300 cursor-not-allowed'}`}
          title="Borrar lote">
          <TrashIcon className="h-5 w-5" />
        </button>

        {selectedLote && (
          <span className="text-xs text-gray-500 ml-2">
            Total: {selectedLote.total}
          </span>
        )}
      </div>

      {takeoffLoading && <LoadingTables />}

      <div className="flex-grow overflow-y-auto overflow-x-auto">
        <table className="min-w-[1400px] w-full table-auto text-xs">
          <thead className="sticky top-0 z-10 bg-gray-100 text-xs uppercase text-gray-700">
            <tr>
              <th className="px-1 py-1 text-center font-semibold w-8">#</th>
              <th className="px-1 py-1 text-left font-semibold w-16">Reporte</th>
              <th className="px-1 py-1 text-center font-semibold w-14">Fecha</th>
              <th className="px-1 py-1 text-left font-semibold w-16">OT</th>
              <th className="px-1 py-1 text-left font-semibold w-28">Ruta</th>
              <th className="px-1 py-1 text-left font-semibold w-28">Cliente</th>
              <th className="px-1 py-1 text-right font-semibold w-20">Tarifa</th>
              <th className="px-1 py-1 text-right font-semibold w-12">Fracc</th>
              <th className="px-1 py-1 text-right font-semibold w-24">Total TK</th>
              <th className="px-1 py-1 text-right font-semibold w-24">Total Sol</th>
              <th className="px-1 py-1 text-center font-semibold w-14">Δ</th>
              <th className="px-1 py-1 text-center font-semibold w-20">Estado</th>
              <th className="px-1 py-1 text-center font-semibold w-16">Acc</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {takeoffRows.length === 0 && !takeoffLoading ? (
              <tr>
                <td className="px-2 py-3 text-center text-sm text-gray-500" colSpan={13}>
                  Sin registros
                </td>
              </tr>
            ) : (
              takeoffRows.map((row, index) => {
                // Extraer datos anidados
                const takeoff = getTakeoff(row);
                const documento = getDocumento(row);
                const solicitud = getSolicitud(row);
                const vehiculos = getVehiculos(row);

                // Campos del takeoff
                const reporte = takeoff?.REPORTE || takeoff?.reporte || '';
                const valorTarifa = takeoff?.VALOR_TARIFA || takeoff?.valor_tarifa || 0;
                const fraccion = takeoff?.FRACCION || takeoff?.fraccion || 0;
                const totalTK = takeoff?.TOTAL || takeoff?.total || 0;
                const estadoRaw = takeoff?.ESTADO ?? takeoff?.estado ?? 0;
                const estadoValue = estadoRaw === null || estadoRaw === undefined ? '0' : String(estadoRaw);

                // Campos del documento
                const fechaDoc = documento?.DOCUMENTO_FECHA || '';

                // Campos de la solicitud
                const otInterna = solicitud?.SOLICITUD_CONSECUTIVO_INT || '';
                const origen = solicitud?.ORIGEN || '';
                const destino = solicitud?.DESTINO || '';
                const cliente = solicitud?.CLIENTE_NOMBRE || '';
                const totalSol = solicitud?.SOLICITUD_TOTAL || 0;

                // Cálculo de diferencia
                const { percent, color } = calcDiff(totalTK, totalSol);

                const isUpdating = takeoffUpdating?.[reporte];
                const isActionDisabled = !reporte;

                const ruta = origen && destino ? `${origen} → ${destino}` : '-';
                const observaciones = getObservaciones(row);
                const observacionesTitle = observaciones || undefined;

                return (
                  <React.Fragment key={reporte || index}>
                    {/* Fila principal */}
                    <tr className={`border-t border-gray-300 ${
                      solicitud?.SOLICITUD_APROBADA === 6
                        ? 'bg-green-50 hover:bg-green-100'
                        : 'bg-white hover:bg-blue-50'
                    }`}>
                      <td className="px-1 py-1 text-center tabular-nums">{index + 1}</td>
                      <td className="px-1 py-1 text-left font-medium" title={observacionesTitle}>
                        {reporte}
                      </td>
                      <td className="px-1 py-1 text-center tabular-nums">{formatDateShort(fechaDoc)}</td>
                      <td className="px-1 py-1 text-left">{otInterna || '-'}</td>
                      <td className="px-1 py-1 text-left">{ruta}</td>
                      <td className="px-1 py-1 text-left">{cliente || '-'}</td>
                      <td className="px-1 py-1 text-right tabular-nums">{moneyFormat(valorTarifa)}</td>
                      <td className="px-1 py-1 text-right tabular-nums">{fraccion}</td>
                      <td className="px-1 py-1 text-right tabular-nums font-medium">{moneyFormat(totalTK)}</td>
                      <td className="px-1 py-1 text-right tabular-nums">{moneyFormat(totalSol)}</td>
                      <td className={`px-1 py-1 text-center tabular-nums rounded ${color}`}>
                        {percent.toFixed(1)}%
                      </td>
                      <td className="px-1 py-1 text-center">
                        <select
                          value={estadoValue}
                          onChange={(event) => handleUpdateTakeoffEstado(reporte, event.target.value)}
                          disabled={isUpdating || !reporte}
                          className="rounded border px-1 py-0.5 text-xs w-full">
                          {estadoOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-1 text-center">
                        <div className="flex justify-center gap-1">
                          {/* Finalizar indicator - shows for APROBADA 5 (clickable) and 6 (static icon) */}
                          {(solicitud?.SOLICITUD_APROBADA === 5 || solicitud?.SOLICITUD_APROBADA === 6) && (() => {
                            const solicitudId = solicitud?.SOLICITUD_ID;
                            const isFinalizada = solicitud?.SOLICITUD_APROBADA === 6;
                            const isFinalizing = takeoffFinalizing[solicitudId];

                            // Static badge icon for finalized requests
                            if (isFinalizada) {
                              return (
                                <div
                                  title="Solicitud finalizada"
                                  className="p-0.5 text-green-600">
                                  <CheckBadgeIcon className="h-5 w-5" />
                                </div>
                              );
                            }

                            // Clickable button for active requests
                            return (
                              <button
                                type="button"
                                onClick={() => handleFinalizarTakeoff(row)}
                                disabled={isFinalizing}
                                title={isFinalizing ? "Finalizando..." : "Finalizar solicitud"}
                                className={`p-0.5 text-green-600 hover:text-green-800 ${isFinalizing ? 'opacity-40 cursor-wait' : ''}`}>
                                {isFinalizing ? (
                                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircleIcon className="h-4 w-4" />
                                )}
                              </button>
                            );
                          })()}

                          {/* Existing delete button */}
                          <button
                            type="button"
                            onClick={() => handleDeleteTakeoffReporte(reporte)}
                            disabled={isActionDisabled}
                            title="Borrar"
                            className={`p-0.5 text-red-600 hover:text-red-800 ${isActionDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Sub-tabla de vehículos */}
                    {vehiculos.length > 0 && (
                      <tr className="bg-white">
                        <td colSpan={13} className="p-0 pb-2">
                          <VehiculosSubTable
                            vehiculos={vehiculos}
                            solicitud={solicitud}
                            reporte={reporte}
                            onOpenManifiesto={handleManifiestoClick}
                            onOpenTarifas={handleTarifasClick}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="shrink-0 pt-2">
        {takeoffPagination && takeoffPagination.totalPages > 1 && (
          <Pager
            data={{ pagination: takeoffPagination }}
            pager={{ handleNextPage, handlePreviusPage }}
            canQuery={false}
          />
        )}
      </div>

      <DetalleTakeoff
        data={{ detail: takeoffDetail, loading: takeoffDetailLoading, isOpen: takeoffDetailOpen }}
        actions={{ handleClose: handleCloseTakeoffDetail }}
      />
    </div>
  );
};

export default TakeoffTab;
